import { SETTLEMENT_SOURCE_PRIORITY, type CashFlowSource } from '@progear/finance-core';

/**
 * Settlement-source presentation metadata (module 1.3 / 1.3e). The ONE place the
 * four settlement sources are NAMED and STYLED for the UI — the register `Allikas`
 * column, the enriched-detail per-source overview and the shared hover card all
 * read this, so a source looks and reads identically in BOTH lenses (L14, master
 * rule: don't reinvent terminology). finance-core owns WHICH source wins
 * (`effectivePaymentSourceOf`) and the priority ORDER
 * ({@link SETTLEMENT_SOURCE_PRIORITY}); this only labels + styles. Inherited
 * dark-dense badge classes (L8) reuse the settlement design tokens.
 */
export interface SettlementSourceMeta {
  source: CashFlowSource;
  /** Full Estonian label (filter option · hover-card · detail row). */
  label: string;
  /** Compact code for the register column badge. */
  short: string;
  /** Badge classes (border + bg + text), inherited settlement tokens (L8). */
  badge: string;
  /** Status-dot colour class. */
  dot: string;
}

/** The four ranked settlement sources, keyed for O(1) lookup. */
const META: Partial<Record<CashFlowSource, SettlementSourceMeta>> = {
  lhv: {
    source: 'lhv',
    label: 'LHV pank',
    short: 'LHV',
    badge: 'border-[hsl(var(--settled))]/40 bg-[hsl(var(--settled))]/12 text-[hsl(var(--settled))]',
    dot: 'bg-[hsl(var(--settled))]',
  },
  smartaccounts: {
    source: 'smartaccounts',
    label: 'SmartAccounts',
    short: 'SA',
    badge: 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]',
    dot: 'bg-[hsl(var(--primary))]',
  },
  erply: {
    source: 'erply',
    label: 'Erply',
    short: 'ERP',
    badge: 'border-[hsl(var(--vatmode))]/40 bg-[hsl(var(--vatmode))]/12 text-[hsl(var(--vatmode))]',
    dot: 'bg-[hsl(var(--vatmode))]',
  },
  manual: {
    source: 'manual',
    label: 'Manuaalne',
    short: 'MAN',
    badge: 'border-border bg-muted/40 text-muted-foreground',
    dot: 'bg-muted-foreground/60',
  },
};

/** A neutral fallback so an unexpected settlement source still renders (never a
 *  blank cell) — labelled by its raw key, styled muted like a manual entry. */
function fallbackMeta(source: CashFlowSource): SettlementSourceMeta {
  return {
    source,
    label: source,
    short: source.slice(0, 3).toUpperCase(),
    badge: 'border-border bg-muted/30 text-muted-foreground',
    dot: 'bg-muted-foreground/60',
  };
}

/** Presentation metadata for a settlement source (fallback for unranked ones). */
export function settlementSourceMeta(source: CashFlowSource): SettlementSourceMeta {
  return META[source] ?? fallbackMeta(source);
}

/**
 * The four settlement sources in TRUTH-priority order (LHV → SmartAccounts →
 * Erply → Manuaal), derived from finance-core's {@link SETTLEMENT_SOURCE_PRIORITY}
 * so the order has ONE owner (master rule). Drives the detail per-source overview
 * and the filter facet options.
 */
export const SETTLEMENT_SOURCE_META_LIST: SettlementSourceMeta[] =
  SETTLEMENT_SOURCE_PRIORITY.map((s) => settlementSourceMeta(s));
